import { 
    InsertDriveFile,
    Image as ImageIcon,
    PictureAsPdf,
    Close as CloseIcon,
    Download as DownloadIcon
  } from "@mui/icons-material";
  import { 
    Box, 
    IconButton, 
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Stack
  } from "@mui/material";
  import axiosInstance from "../../../utils/axios";
import { useEffect, useRef, useState } from "react";
import { saveAs } from 'file-saver';


const DocumentModal = ({ open, onClose, document }) => {
    const [loading, setLoading] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [error, setError] = useState(null);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const imageRef = useRef(null)
  
    const handleDownload = async () => {
      if (!document?.upload_id) return;
  
      setLoading(true);
      setError(null);
  
      try {
        const response = await axiosInstance.get(
          `/file_upload/download?image_id=${document.upload_id}`
        );
  
        if(response?.data) {
            setImageUrl(response?.data);
        }
       
      } catch (error) {
        console.error('Download error:', error);
        setError('Failed to load document. Please try again.');
      } finally {
        setLoading(false);
      }
    };
  
    const handleDirectDownload = async () => {
      try {
        setDownloadLoading(true);
    
        const response = await axiosInstance.get(
          `/file_upload/download?image_id=${document.upload_id}`,
          { responseType: 'blob' }
        );
    
        const contentType = response.headers['content-type'];
    
        if (contentType.includes('application/json')) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const json = JSON.parse(reader.result);
              console.error("Download failed:", json);
              setError(json?.message || 'Failed to download file.');
            } catch {
              setError('Received invalid response while downloading.');
            }
          };
          reader.readAsText(response.data);
          return;
        }
    
        const blob = new Blob([response.data], { type: contentType });
        saveAs(blob, document.name || 'downloaded-file');
    
      } catch (error) {
        console.error('Download failed:', error);
        setError('Failed to download file.');
      } finally {
        setDownloadLoading(false);
      }
    };    

    useEffect(() => {
        if(document && open) {
            handleDownload();
        }
        // eslint-disable-next-line
    }, [document, open]);

    useEffect(() => {
        if (!open) {
            setImageUrl(null);
            setError(null);
            setLoading(false);
            setDownloadLoading(false);
        }
    }, [open]);
  
    const handleClose = () => {
      setImageUrl(null);
      setError(null);
      setLoading(false);
      setDownloadLoading(false);
      onClose();
    };
  
    const getFileIcon = (fileType) => {
      if (fileType?.includes('pdf')) {
        return <PictureAsPdf sx={{ color: '#d32f2f', fontSize: 40 }} />;
      } else if (fileType?.includes('image')) {
        return <ImageIcon sx={{ color: '#1976d2', fontSize: 40 }} />;
      }
      return <InsertDriveFile sx={{ color: '#757575', fontSize: 40 }} />;
    };
  
    const formatFileSize = (bytes) => {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const isImageFile = (fileType, fileName) => {
      if (fileType?.includes('image')) return true;
      if (fileName) {
        const extension = fileName.toLowerCase().split('.').pop();
        return ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(extension);
      }
      return false;
    };

    const isPdfFile = (fileType, fileName) => {
      if (fileType?.includes('pdf')) return true;
      if (fileName) {
        const extension = fileName.toLowerCase().split('.').pop();
        return extension === 'pdf';
      }
      return false;
    };

    const currentFileIsImage = isImageFile(document?.type, document?.name);
    const currentFileIsPdf = isPdfFile(document?.type, document?.name);
  
    return (
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '500px', maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6">Document Viewer</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDirectDownload}
              disabled={downloadLoading || loading || !imageUrl}
              size="small"
            >
              {downloadLoading ? 'Downloading...' : 'Download'}
            </Button>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 1 }}>
          {document && (
            <Box>
              {/* Document Info */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  {getFileIcon(document.type)}
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {document.name}
                    </Typography>
                    <Stack direction="row" spacing={1} mt={1}>
                      <Chip 
                        label={formatFileSize(document.size)} 
                        size="small" 
                        variant="outlined"
                      />
                      {document.type && (
                        <Chip 
                          label={document.type.split('/')[1]?.toUpperCase() || 'FILE'} 
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      <Chip 
                        label={`ID: ${document.upload_id}`} 
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {loading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, height: '400px', justifyContent: "center" }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="textSecondary" mt={2}>
                    Loading document...
                  </Typography>
                </Box>
              )}

              {!loading && (
                <>
                  {imageUrl && currentFileIsImage && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                          bgcolor: 'grey.50',
                          maxHeight: '500px',
                          overflow: 'hidden'
                        }}
                      >
                        <Box
                          component="img"
                          ref={imageRef}
                          src={imageUrl}
                          alt={document.name}
                          sx={{
                            width: '600px',
                            height: '400px',
                            objectFit: 'contain',
                            borderRadius: 1,
                          }}
                          onError={() => setError('Failed to load image preview')}
                        />
                      </Box>
                    </Box>
                  )}

                  {imageUrl && currentFileIsPdf && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight={500}>
                        PDF Document:
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 3,
                          bgcolor: 'grey.50',
                          minHeight: '300px'
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <PictureAsPdf sx={{ fontSize: 80, color: '#d32f2f', mb: 2 }} />
                          <Typography variant="h6" gutterBottom>
                            PDF Document
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            {document.name}
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDirectDownload}
                            disabled={downloadLoading}
                            sx={{ mt: 2 }}
                          >
                            {downloadLoading ? 'Downloading...' : 'Download PDF'}
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {imageUrl && !currentFileIsImage && !currentFileIsPdf && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Box sx={{ mb: 2 }}>
                        {getFileIcon(document.type)}
                      </Box>
                      <Typography variant="h6" gutterBottom>
                        {document.name}
                      </Typography>
                      <Typography variant="body1" color="textSecondary" gutterBottom>
                        This file type cannot be previewed.
                      </Typography>
                      <Typography variant="body2" color="textSecondary" mb={2}>
                        Click the download button to save the file.
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDirectDownload}
                        disabled={downloadLoading}
                      >
                        {downloadLoading ? 'Downloading...' : 'Download File'}
                      </Button>
                    </Box>
                  )}

                  {!imageUrl && !error && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        No content available for this document.
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  export default DocumentModal;